<template>
  <div
    v-if="isLoading"
    class="db-tw-flex db-tw-justify-center"
  >
    <DbSpinner size="50" />
  </div>
  <div
    v-if="!isLoading && cartSummary"
    class="db-reservation-summary"
    :class="{ 'db-has-infos': $slots.reservationinfos }"
  >
    <!-- Title + button info + button edit -->
    <p class="db-reservation-summary-title db-bold">
      {{ cartSummary.packageName }}
      <button
        v-if="packageData"
        type="button"
        class="db-button-text-link db-reservation-summary-package-infos"
        :title="$t('Package infos')"
        v-on:click="showModalPackage(false)"
      >
        <IconDbInfo
          width="16"
          height="16"
        />
      </button>

      <button
        v-if="showEditionOptions"
        type="button"
        class="db-button-text-link db-button-square db-reservation-summary-package-edit"
        v-on:click="$emit('edit-room')"
      >
        <span class="db-visually-hidden">
          {{ $t('Edit package') }}
        </span>
        <IconDbEdit
          width="16"
          height="16"
        />
      </button>
    </p>
    <!-- !Title + button info + button edit -->

    <!-- Informations bloc -->
    <div
      v-if="$slots.reservationinfos"
      class="db-reservation-summary-infos"
    >
      <!-- @slot Reservation info block, used for Confirmation number / name -->
      <slot name="reservationinfos" />
    </div>
    <!-- !Informations bloc -->

    <!-- Package informations -->
    <ul class="db-reservation-summary-list db-reservation-summary-meeting">
      <li
        class="db-reservation-summary-item db-reservation-summary-item--fullline"
        v-if="configStore.funnel.showVenueSelection"
      >
        {{ $t('Venue:') }}&nbsp;
        <!-- <button
          type="button"
          class="db-button-text-link"
          v-on:click="null /* Open modal venue */"
        >
          {{ reservation.venue }}
        </button> -->
        <span
          v-if="cartSummary.venueName && currentFunctionRoom?.galaxyEventRoom"
          v-on:click="showDetailModal(currentFunctionRoom?.galaxyEventRoom)"
          class="db-button-text-link"
        >
          {{ cartSummary.venueName }}
        </span>
        <span
          v-else-if="cartSummary.venueName"
          class="db-reservation-summary-item--fullline"
        >
          {{ cartSummary.venueName }}
        </span>
        <span
          v-else
          class="db-tw-opacity-75"
        >
          {{ $t('selecting...') }}
        </span>
      </li>
      <li class="db-reservation-summary-item db-reservation-summary-item--fullline">
        {{ $t('Event Date: {x}', { x: formattedDate }) }}
      </li>
      <li
        v-if="cartSummary.timeSlot && cartSummary.timeSlot?.label"
        class="db-reservation-summary-item db-reservation-summary-item--fullline"
      >
        {{ $t('Time Slot: {x}', { x: cartSummary.timeSlot?.label }) }}
      </li>
      <li class="db-reservation-summary-item">
        {{ $t('Attendees: {x}', { x: cartSummary.attendees }) }}
      </li>
      <li class="db-reservation-summary-item">
        {{ cartSummary.formattedPricing?.packageBundle }}
      </li>
      <li
        v-if="cartSummary.functionRoomSetup"
        class="db-reservation-summary-item"
      >
        {{ $t('Room Setup: {x}', { x: cartSummary.functionRoomSetup }) }}
      </li>
      <li
        v-if="cartSummary.functionRoomSetup"
        class="db-reservation-summary-item"
      >
        {{ $t('Included') }}
      </li>
    </ul>
    <!-- !Package informations -->

    <!-- Package inclusions -->
    <DbDisclosure
      v-if="cartSummary.inclusions.length"
      class="db-reservation-summary-package"
      :expanded="isPackageInclusionsExpanded"
      v-on:click="() => (isPackageInclusionsExpanded = !isPackageInclusionsExpanded)"
    >
      <template v-slot:button>
        <span class="db-reservation-summary-title">
          <span class="db-reservation-disclosure-title">{{ $t('Package Inclusions') }}</span>

          <IconDbChevron
            :class="{
              'db-rotate-90': isPackageInclusionsExpanded,
              'db-rotate-270': !isPackageInclusionsExpanded,
            }"
            class="db-reservation-summary-icon-chevron"
            width="10"
            height="10"
          />
        </span>
      </template>

      <ul class="db-reservation-summary-list">
        <li
          v-for="(inclusion, index) in cartSummary.inclusions"
          :key="`inclusion${index}`"
          class="db-reservation-summary-item"
        >
          {{ inclusion }}
        </li>
      </ul>
    </DbDisclosure>
    <div
      v-else
      class="db-reservation-summary-package"
    ></div>
    <!-- !Package inclusions -->

    <!-- Addons -->
    <!-- Appears if addonsLength exists -->
    <DbDisclosure
      v-if="cartSummary.addons.length"
      class="db-reservation-summary-addons"
      :expanded="isAddonsExpanded"
      v-on:click="() => (isAddonsExpanded = !isAddonsExpanded)"
    >
      <template v-slot:button>
        <span class="db-reservation-summary-title">
          <span class="db-reservation-disclosure-title">{{ $t('Add-ons') }}</span>

          <IconDbChevron
            :class="{
              'db-rotate-90': isAddonsExpanded,
              'db-rotate-270': !isAddonsExpanded,
            }"
            class="db-reservation-summary-icon-chevron"
            width="10"
            height="10"
          />
        </span>
      </template>

      <!-- Addons list -->
      <ul class="db-reservation-summary-list">
        <li
          v-for="(addon, index) in cartSummary.addons"
          :key="`inclusion${index}`"
          class="db-reservation-summary-item db-reservation-summary-addon"
        >
          <span>
            {{ addon.name }}
          </span>
          <span>
            {{ addon.formattedPrice }}
          </span>
          <span class="db-reservation-summary-addon-details">
            {{ addon.formattedUnitPrice }} x {{ addon.quantity }}
          </span>
        </li>
      </ul>
      <!-- !Addons list -->
    </DbDisclosure>
    <!-- !Addons -->

    <!-- Delete addons button -->
    <div class="db-reservation-summary-addons-edition">
      <button
        v-if="allowAddonsEdition && cartSummary.addons.length"
        class="db-button-text-link db-button-square db-reservation-summary-addons-edit"
        :title="$t('Edit Add-ons')"
        v-on:click="$emit('edit-addons')"
      >
        <IconDbEdit
          width="16"
          height="16"
        />
      </button>
      <button
        v-if="showEditionOptions && cartSummary.addons.length"
        class="db-button-square db-reservation-summary-addons-delete"
        :title="$t('Delete Add-ons')"
        v-on:click="$emit('delete-addons')"
      >
        <IconDbDelete
          width="13"
          height="13"
        />
      </button>
    </div>
    <!-- !Addons -->

    <!-- Detail rate summary -->
    <table class="db-reservation-summary-table">
      <tbody>
        <tr
          v-if="cartSummary.formattedPricing?.subTotal"
          class="db-reservation-summary-table-subtotal"
        >
          <th scope="row">
            {{ $t('Reservation Subtotal') }}
          </th>
          <td>{{ cartSummary.formattedPricing?.subTotal }}</td>
        </tr>
        <tr
          v-if="cartSummary.formattedPricing?.taxes"
          class="db-reservation-summary-table-taxes db-reservation-summary-table-section-tax"
        >
          <th scope="row">
            {{ $t('Taxes') }}
          </th>
          <td>{{ cartSummary.formattedPricing?.taxes }}</td>
        </tr>
        <tr
          v-else-if="!(cartSummary.venueName && currentFunctionRoom?.galaxyEventRoom)"
          class="db-reservation-summary-table-taxes db-reservation-summary-table-section-tax"
        >
          <th scope="row">
            {{ $t('Taxes') }}
          </th>
          <td class="db-tw-opacity-75">{{ $t('...') }}</td>
        </tr>
        <tr
          v-if="cartSummary.formattedPricing?.gratuityFee"
          class="db-reservation-summary-table-gratuityfee db-reservation-summary-table-section-tax"
        >
          <th scope="row">
            {{ $t('Gratuity fee') }}
          </th>
          <td>{{ cartSummary.formattedPricing?.gratuityFee }}</td>
        </tr>
        <tr
          v-if="cartSummary.formattedPricing?.adminFee"
          class="db-reservation-summary-table-adminfee db-reservation-summary-table-section-tax"
        >
          <th scope="row">
            {{ $t('Admin fee') }}
          </th>
          <td>{{ cartSummary.formattedPricing?.adminFee }}</td>
        </tr>

        <tr
          v-if="cartSummary.formattedPricing?.totalReservation"
          class="db-reservation-summary-table-total"
        >
          <th scope="row">
            {{ $t('Total Reservation') }}
          </th>
          <td>{{ cartSummary.formattedPricing?.totalReservation }}</td>
        </tr>

        <tr
          v-else-if="!(cartSummary.venueName && currentFunctionRoom?.galaxyEventRoom)"
          class="db-reservation-summary-table-total"
        >
          <th scope="row">
            {{ $t('Total Reservation') }}
          </th>
          <td class="db-tw-opacity-75">{{ $t('...') }}</td>
        </tr>
        <tr
          v-if="
            cartSummary.formattedPricing?.payNow !== undefined &&
            general.payPercentage > 0 &&
            general.payPercentage < 100
          "
          class="db-reservation-summary-table-paynow"
        >
          <th scope="row">
            {{ $t('Pay now ({x}%)', { x: general.payPercentage }) }}
          </th>
          <td>{{ cartSummary.formattedPricing?.payNow }}</td>
        </tr>
        <tr
          v-if="
            cartSummary.formattedPricing?.payLater !== undefined &&
            general.payPercentage > 0 &&
            general.payPercentage < 100
          "
          class="db-reservation-summary-table-paylater"
        >
          <th scope="row">
            {{ $t('Pay upon arrival ({x}%)', { x: 100 - general.payPercentage }) }}
          </th>
          <td>{{ cartSummary.formattedPricing?.payLater }}</td>
        </tr>
      </tbody>
    </table>
    <!-- Detail rate summary -->
  </div>
  <div v-if="!isLoading && cartSummary && showEditionOptions">
    <button
      type="button"
      class="db-start-over-button db-button-text-supporting db-bold db-item-start db-tw-m-4"
      v-on:click="$emit('start-over')"
    >
      <IconDbRestore
        width="22"
        height="22"
      />
      {{ $t('Start over') }}
    </button>
  </div>
  <div v-if="!isLoading && !cartSummary">
    <p class="db-text-center db-tw-pt-3 db-tw-pb-5">
      {{ $t('No items in cart') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, computed } from 'vue'
import type { CartSummary } from './DbReservationSummaryTypes'
import type { GalaxyPackage } from '@/types/galaxy-data/packages'
import { useModalsStore } from '@/stores/modals'
import { useCartStore } from '@/stores/cart'
import { storeToRefs } from 'pinia'
import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import { useConfigurations } from '@/stores/configurations'
import { useI18n } from 'vue-i18n'
import { applyDateFnsConverter, doDateFnsFormatTranslate } from '@/composables/helpers/useDate'

const configStore = useConfigurations()

const { general } = configStore

const props = withDefaults(
  defineProps<{
    isLoading?: boolean
    cartSummary?: CartSummary
    /** Show or hide edit / delete buttons */
    showEditionOptions?: boolean
    /** If the button to edit addons can be shown */
    allowAddonsEdition?: boolean
    openDropdownsByDefault?: boolean
    packageData?: GalaxyPackage
  }>(),
  {
    isLoading: false,
    cartSummary: undefined,
    showEditionOptions: false,
    allowAddonsEdition: false,
    openDropdownsByDefault: true,
  },
)

defineEmits<{
  (e: 'edit-addons'): void
  (e: 'edit-room'): void
  (e: 'start-over'): void
  (e: 'delete-addons'): void
}>()

const { locale, t } = useI18n()

const formattedDate = computed(() => {
  const formatterWeekday = new Intl.DateTimeFormat(locale.value, {
    weekday: 'long',
  })
  // Don't put "Day of Week" prefix, when he is always defined though the dateFormat
  if (
    configStore.general.dateFormat !== 'EEE, MMM dd, yyyy' &&
    configStore.general.dateFormat !== 'EEE, MMM dd'
  ) {
    return (
      t(formatterWeekday.format(props.cartSummary?.date)).slice(0, 3) +
      ', ' +
      doDateFnsFormatTranslate(
        props.cartSummary?.date ? props.cartSummary?.date : new Date(),
        applyDateFnsConverter(configStore.general.dateFormat),
      )
    )
  }
  return doDateFnsFormatTranslate(
    props.cartSummary?.date ? props.cartSummary?.date : new Date(),
    applyDateFnsConverter(configStore.general.dateFormat),
  )
})

/*  */
const isPackageInclusionsExpanded = ref<boolean>()
const isAddonsExpanded = ref<boolean>()

onBeforeMount(() => {
  isPackageInclusionsExpanded.value = props.openDropdownsByDefault
  isAddonsExpanded.value = props.openDropdownsByDefault
})

const modalStore = useModalsStore()

function showModalPackage(event_tracking = true) {
  if (!props.packageData) {
    throw new Error('No current package')
  }
  modalStore.computeFunctionRoomSetupsFromCurrentPackage()
  modalStore.showPackageModal(props.packageData, event_tracking)
}

const cartStore = useCartStore()
const { currentFunctionRoom, currentPackage } = storeToRefs(cartStore)

async function showDetailModal(meetingRoom: GalaxyMeetingRoom) {
  modalStore.computeFunctionRoomSetupsFromCurrentPackage() // Compute to define functionRoomSetups (use in room setup section of the modal)
  modalStore.showMeetingRoomModal(
    meetingRoom,
    currentPackage.value?.galaxyPackage?.functionRoomSetups,
  )
}
</script>

<style>
.db-app .db-reservation-summary {
  /* Global */
  --db-font-size-reservation-summary: var(--db-spacer-12);
  --db-padding-inline-reservation-summary: var(--db-spacer-16);

  /* Details */
  --db-border-radius-reservation-summary-infos: 0;

  /* Tax breakdown button */
  --db-font-size-reservation-summary-tax-breakdown-button: var(
    --db-font-size-12
  ); /* TODO: xo7: mobile text var used for small text */
}

.db-app .db-confirmation .db-reservation-summary {
  /* Global */
  --db-font-size-reservation-summary: var(--db_body_text_font_size);
}

.db-app .db-reservation-summary-infos p {
  color: var(--db_box_body_text_color);
}

@media screen and (max-width: 992px) {
  .db-app .db-reservation-summary {
    /* Global */
    --db-font-size-reservation-summary: var(--db_small_body_text_font_size);

    /* Details */
    --db-border-radius-reservation-summary-infos: 6px;

    /* Tax breakdown button */
    --db-font-size-reservation-summary-tax-breakdown-button: var(--db_small_body_text_font_size);
  }
}
</style>
<style scoped>
.db-app .db-reservation-summary {
  position: relative;
  display: grid;
  row-gap: var(--db-spacer-8);
  grid-template-columns:
    var(--db-padding-inline-reservation-summary)
    1fr
    var(--db-padding-inline-reservation-summary);
  font-size: var(--db-font-size-reservation-summary);
}

.db-app .db-reservation-summary > * {
  grid-column: 2;
}

/* Global font size extended */
.db-app .db-reservation-summary p,
.db-app .db-reservation-summary .db-button-text-link {
  font-size: var(--db-font-size-reservation-summary);
}

/* Title lines (main title and disclosure buttons) */
.db-app .db-reservation-summary .db-reservation-summary-title {
  color: var(--db_body_text_color);
  font-size: var(--db-font-size-reservation-summary);
  display: flex;
  font-weight: bold;
  align-items: center;
  gap: var(--db-spacer-6);
  height: var(--db-size-button-default);
}

.db-app .db-reservation-summary-title [class*='button'] {
  height: 100%;
}

.db-app .db-reservation-summary-package-edit {
  margin-inline-start: auto;
  margin-inline-end: calc(var(--db-spacer-10) * -1);
}
/* !Title line */

/* Gray info bloc */
.db-app .db-reservation-summary-infos {
  background-color: var(--db_box_background_color);
  padding-inline: var(--db-spacer-8);
  padding-block: var(--db-spacer-12);
  border-radius: var(--db-border-radius-reservation-summary-infos);
  display: grid;
  row-gap: var(--db-spacer-8);
  margin-inline: calc(var(--db-spacer-8) * -1);
}

/* List of items */
.db-app .db-reservation-summary-list {
  display: grid;
  gap: var(--db-spacer-16);
}

/* List item */
.db-app .db-reservation-summary-item {
  font-size: var(--db-font-size-reservation-summary);
}

/* Spacing in disclosure */
.db-app .db-reservation-summary .db-disclosure-wrapper > * {
  padding-block: var(--db-spacer-6);
}

/* Meeting details */
.db-app .db-reservation-summary-meeting {
  gap: var(--db-spacer-12);
  grid-template-columns: 1fr auto;
}
.db-app .db-reservation-summary-meeting .db-reservation-summary-item--fullline {
  grid-column: 1 / span 2;
}

/* Addons */
.db-app .db-reservation-summary-addons,
.db-app .db-reservation-summary-addons-edition {
  grid-row: 4;
}

.db-app .db-has-infos .db-reservation-summary-addons,
.db-app .db-has-infos .db-reservation-summary-addons-edition {
  grid-row: 5;
}

.db-app .db-reservation-summary-addons-edition {
  display: grid;
  grid-auto-flow: column;
  margin-inline-end: calc(var(--db-spacer-10) * -1);
  align-self: start;
  justify-self: end;
}

.db-app .db-reservation-summary-addons-delete {
  color: var(--db_inline_error_message_text_color);
}

/* Addon list item */
.db-app .db-reservation-summary-addon {
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: var(--db-spacer-6);
}

.db-app .db-reservation-summary-addon-details {
  grid-column: 1 / span 2;
  color: var(--db_secondary_text_color);
  padding-inline-start: var(--db-spacer-10);
}

/* Price table */
.db-app .db-reservation-summary-table {
  grid-column: 1 / span 3;
}

.db-app .db-reservation-summary-table th,
.db-app .db-reservation-summary-table td {
  padding-inline: var(--db-padding-inline-reservation-summary);
  padding-block-start: var(--db-spacer-8);
}

.db-app .db-reservation-summary-table td {
  text-align: end;
}

/* Subtotal */
.db-app .db-reservation-summary-table .db-reservation-summary-table-subtotal {
  border-top: 1px solid var(--db_borders_color);
  border-bottom: 1px solid var(--db_borders_color);
}

.db-app .db-reservation-summary-table-subtotal + tr > * {
  padding-block-start: var(--db-spacer-12);
}

.db-app .db-reservation-summary-table .db-reservation-summary-table-subtotal > * {
  padding-block: var(--db-spacer-16);
}

.db-app
  .db-reservation-summary-table
  tr:nth-last-child(-n + 1 of .db-reservation-summary-table-section-tax)
  > * {
  padding-block-end: var(--db-spacer-16);
}

.db-app .db-reservation-summary-table .db-reservation-summary-table-total > * {
  background-color: var(--db_box_background_color);
  font-weight: bold;
  font-size: var(--db_body_text_font_size);
  color: var(--db_box_rate_color);
  padding-block: var(--db-spacer-16);
}

.db-app .db-reservation-summary-table .db-reservation-summary-table-paylater > * {
  padding-block-end: var(--db-spacer-16);
  border-bottom: 1px solid var(--db_borders_color);
}

/* Tax breakdown button */
.db-app .db-reservation-summary .db-tax-breakdown {
  grid-column: 1 / span 3;
  padding-inline: var(--db-padding-inline-reservation-summary);
  padding-block: 0 var(--db-spacer-16);
  background-color: var(--db_box_background_color);
  margin-top: calc(var(--db-spacer-16) * -1);
}

.db-app .db-reservation-summary .db-tax-breakdown-button {
  color: var(--db_box_link_color);
  font-size: var(--db-font-size-reservation-summary-tax-breakdown-button);
}

.db-app .db-reservation-disclosure-title {
  font-family: var(--db_body_text_font_family);
}
</style>
